/* eslint-disable */
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Select,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { SearchIcon } from '@chakra-ui/icons';
import { MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card/Card';
import React, { useMemo } from 'react';
import { formatDate } from 'utils/format';

const columnHelper = createColumnHelper();

export default function UsersTable(props) {
  const { tableData = [], pagination, onPageChange, onItemsPerPageChange } = props;
  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const bgStatus = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const inputBg = useColorModeValue('secondaryGray.300', 'navy.900');
  const inputText = useColorModeValue('secondaryGray.700', 'white');
  const searchIconColor = useColorModeValue('secondaryGray.700', 'white');
  const buttonBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const buttonHover = useColorModeValue(
    { bg: 'secondaryGray.400' },
    { bg: 'whiteAlpha.50' }
  );
  const iconColor = useColorModeValue('brand.500', 'white');

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'purple';
      case 'center_admin':
        return 'blue';
      case 'blogger':
        return 'green';
      default:
        return 'gray';
    }
  };

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          NAME
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          EMAIL
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('role', {
      id: 'role',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          ROLE
        </Text>
      ),
      cell: (info) => {
        const role = info.getValue();
        return (
          <Badge
            colorScheme={getRoleBadgeColor(role)}
            bg={bgStatus}
            color={textColor}
            fontSize="sm"
            fontWeight="500"
            px="8px"
            py="4px"
            borderRadius="8px"
          >
            {role ? role.replace('_', ' ').toUpperCase() : 'N/A'}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('center', {
      id: 'center',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          CENTER
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('is_active', {
      id: 'is_active',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          STATUS
        </Text>
      ),
      cell: (info) => {
        const isActive = info.getValue();
        return (
          <Badge
            colorScheme={isActive ? 'green' : 'red'}
            bg={bgStatus}
            color={textColor}
            fontSize="sm"
            fontWeight="500"
            px="8px"
            py="4px"
            borderRadius="8px"
          >
            {isActive ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('account_creation', {
      id: 'account_creation',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          CREATED AT
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="500">
          {info.getValue() ? formatDate(info.getValue()) : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="secondaryGray.600"
        >
          ACTIONS
        </Text>
      ),
      cell: (info) => (
        <IconButton
          aria-label="View user"
          icon={<MdVisibility />}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          onClick={() => navigate(`/admin/user/${info.row.original.id}`)}
        />
      ),
    }),
  ];

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) {
      return tableData;
    }

    const query = searchQuery.toLowerCase().trim();
    return tableData.filter((user) => {
      const searchableFields = [
        user.name?.toLowerCase() || '',
        user.email?.toLowerCase() || '',
        user.role?.toLowerCase() || '',
        user.center?.toLowerCase() || '',
      ];

      return searchableFields.some((field) => field.includes(query));
    });
  }, [tableData, searchQuery]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center" flexWrap="wrap" gap="15px">
        <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
          All Users
        </Text>
        <InputGroup w={{ base: "100%", md: "300px" }}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={searchIconColor} w="15px" h="15px" />
          </InputLeftElement>
          <Input
            variant="search"
            fontSize="sm"
            bg={inputBg}
            color={inputText}
            fontWeight="500"
            _placeholder={{ color: "secondaryGray.600", fontSize: "14px" }}
            borderRadius="30px"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </Flex>
      <Box>
        <Table variant="simple" color="secondaryGray.700" mb="24px" mt="12px">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id} borderColor={borderColor} pe="10px"
                    cursor="pointer"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Flex
                      justifyContent="space-between"
                      align="center"
                      fontSize={{ sm: "10px", lg: "12px" }}
                      color="secondaryGray.600"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py="20px">
                  <Text color={textColor} fontSize="sm">
                    No users found
                  </Text>
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      fontSize={{ sm: "14px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Controls */}
      {pagination && (
        <Flex
          px="25px"
          py="20px"
          justifyContent="space-between"
          align="center"
          flexWrap="wrap"
          gap="15px"
          borderTop="1px solid"
          borderColor={borderColor}
        >
          <HStack spacing="10px">
            <Text color={textColor} fontSize="sm" fontWeight="500">
              Items per page:
            </Text>
            <Select
              value={pagination.itemsPerPage}
              onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(Number(e.target.value))}
              w="80px"
              size="sm"
              bg={inputBg}
              color={inputText}
              borderColor={borderColor}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </HStack>

          <HStack spacing="10px">
            <Text color={textColor} fontSize="sm" fontWeight="500">
              {pagination.total > 0 ? (
                <>
                  Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.total)} of{' '}
                  {pagination.total} entries
                </>
              ) : (
                'No entries'
              )}
            </Text>
          </HStack>

          <HStack spacing="5px">
            <Button
              size="sm"
              bg={buttonBg}
              _hover={buttonHover}
              _active={buttonHover}
              onClick={() => onPageChange && onPageChange(pagination.currentPage - 1)}
              isDisabled={pagination.currentPage === 1}
              color={textColor}
            >
              Previous
            </Button>
            <Button
              size="sm"
              bg={buttonBg}
              _hover={buttonHover}
              _active={buttonHover}
              onClick={() => onPageChange && onPageChange(pagination.currentPage + 1)}
              isDisabled={
                pagination.currentPage >= Math.ceil(pagination.total / pagination.itemsPerPage) ||
                pagination.total === 0
              }
              color={textColor}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}
    </Card>
  );
}

